import { Toast as BaseToast } from '@base-ui/react';
import styled from '@emotion/styled';

import { typographyVariantStyles } from '@/design-system/typography-variants';

import { hoverable } from '../../interaction-states';
import { forMobile } from '../../responsive';

export const StyledViewport = styled(BaseToast.Viewport)({
  position: 'fixed',
  zIndex: 1,
  width: '100%',
  maxWidth: '400px',
  top: 32,
  right: 32,
  left: 'auto',
  bottom: 'auto',

  [forMobile]: {
    top: 16,
    right: 16,
    maxWidth: 'calc(100% - 32px)',
  },
});

export const StyledRoot = styled(BaseToast.Root)(({ theme }) => ({
  '--gap': '16px',
  '--peek': '16px',
  '--scale': 'calc(max(0, 1 - (var(--toast-index) * 0.1)))',
  '--shrink': 'calc(1 - var(--scale))',
  '--height': 'var(--toast-frontmost-height, var(--toast-height))',
  '--offset-y':
    'calc(var(--toast-offset-y) + (var(--toast-index) * var(--gap)) + var(--toast-swipe-movement-y))',

  position: 'absolute',
  boxSizing: 'border-box',
  background: theme.colors.background.overlay,
  color: theme.colors.text.primary,
  border: `1px solid ${theme.colors.border.default}`,
  boxShadow: theme.shadows.md,
  padding: '15px 16px',
  width: '100%',
  backgroundClip: 'padding-box',
  borderRadius: theme.radius[16],
  transformOrigin: 'top right',
  top: 0,
  left: 0,
  right: 0,
  WebkitUserSelect: 'none',
  userSelect: 'none',
  transition: 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.5s, height 0.15s',
  cursor: 'default',
  zIndex: 'calc(1000 - var(--toast-index))',
  height: 'var(--height)',
  transform:
    'translateX(var(--toast-swipe-movement-x)) translateY(calc(var(--toast-swipe-movement-y) + (var(--toast-index) * var(--peek)) + (var(--shrink) * var(--height)))) scale(var(--scale))',

  '&[data-expanded]': {
    transform: 'translateX(var(--toast-swipe-movement-x)) translateY(var(--offset-y))',
    height: 'var(--toast-height)',
  },

  '&[data-starting-style], &[data-ending-style]': {
    transform: 'translateY(-150%)',
  },

  '&[data-limited]': {
    opacity: 0,
  },

  '&[data-ending-style]': {
    opacity: 0,

    "&[data-swipe-direction='up']": {
      transform: 'translateY(calc(var(--toast-swipe-movement-y) - 150%))',
    },
    "&[data-swipe-direction='left']": {
      transform:
        'translateX(calc(var(--toast-swipe-movement-x) - 150%)) translateY(var(--offset-y))',
    },
    "&[data-swipe-direction='right']": {
      transform:
        'translateX(calc(var(--toast-swipe-movement-x) + 150%)) translateY(var(--offset-y))',
    },
    "&[data-swipe-direction='down']": {
      transform: 'translateY(calc(var(--toast-swipe-movement-y) + 150%))',
    },
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    width: '100%',
    bottom: '100%',
    left: 0,
    height: 'calc(var(--gap) + 1px)',
  },
}));

export const StyledContent = styled(BaseToast.Content)({
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
  gap: '8px',

  transition: 'opacity 0.25s',

  '&[data-behind]': {
    opacity: 0,
  },

  '&[data-expanded]': {
    opacity: 1,
  },
});

export const StyledContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  flex: 1,
  minWidth: 0,
});

export const StyledActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const StyledTitle = styled(BaseToast.Title)(({ theme }) => ({
  ...typographyVariantStyles.body2,
  fontWeight: 500,
  color: theme.colors.text.primary,
  wordBreak: 'break-word',
  margin: 0,
}));

export const StyledDescription = styled(BaseToast.Description)(({ theme }) => ({
  ...typographyVariantStyles.body3,
  color: theme.colors.text.secondary,
  wordBreak: 'break-word',
  margin: 0,
}));

export const ToastTexts = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  minWidth: 0,
  width: '100%',
});

export const ToastIconWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '20px',
  height: '20px',
  marginTop: '2px',
  color: theme.colors.text.primary,
}));

export const CloseButton = styled('button')(({ theme }) => ({
  ...hoverable(theme),
  width: '20px',
  height: '20px',
  flexShrink: 0,
  background: 'none',
  border: 'none',
  borderRadius: theme.radius[4],
  padding: 0,
  color: theme.colors.text.primary,
  cursor: 'pointer',

  svg: {
    width: '100%',
    height: '100%',
  },
}));
